import { useState, useEffect, useMemo } from "react";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import TabsNavigation from "../../../components/common/TabsNavigation/TabsNavigation";
import Button from "../../../components/common/Button/Button";
import { FiDownload } from "react-icons/fi";
import { getApiInstance } from "../../../utils/api";
import "./CantidadesGeneradas.scss";
import InputDropdown from "../../../components/inputs/InputDropdown/InputDropdown";
import BarChartGraph from "../../../components/common/Charts/BarChartGraph";
import PieChartGraph from "../../../components/common/Charts/PieChartGraph";
import Table from "../../../components/common/Table/Table";

// Las columnas de la tabla se mantienen igual
const columnasResiduos = [
    { key: "residuo", label: "Residuo", visible: true, sortable: true },
    {
        key: "pesoTotal",
        label: "Peso Total (kg)",
        visible: true,
        sortable: true,
        type: "number",
        render: (row) => new Intl.NumberFormat("es-ES").format(row.pesoTotal),
    },
    {
        key: "cantidadValorizada",
        label: "Valorización (kg)",
        visible: true,
        sortable: true,
        type: "number",
        render: (row) =>
            new Intl.NumberFormat("es-ES").format(row.cantidadValorizada),
    },
    {
        key: "cantidadEliminada",
        label: "Eliminación (kg)",
        visible: true,
        sortable: true,
        type: "number",
        render: (row) =>
            new Intl.NumberFormat("es-ES").format(row.cantidadEliminada),
    },
    {
        key: "volumenTotal",
        label: "Volumen (m³)",
        visible: true,
        sortable: true,
        type: "number",
        render: (row) =>
            `${new Intl.NumberFormat("es-ES", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            }).format(row.volumenTotal)} m³`,
    },
    {
        key: "porcentajeValorizacion",
        label: "% Valorización",
        visible: true,
        sortable: false,
        type: "number",
        render: (row) => {
            const percentage =
                row.pesoTotal > 0
                    ? (row.cantidadValorizada / row.pesoTotal) * 100
                    : 0;
            return `${percentage.toFixed(1)}%`;
        },
    },
];

export default function CantidadesGeneradas() {
    const [activeTab, setActiveTab] = useState("grafica");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [informeCantidadesGeneradas, setInformeCantidadesGeneradas] =
        useState([]);

    const [selectedProyecto, setSelectedProyecto] = useState("all");
    const [selectedFecha, setSelectedFecha] = useState("all");
    const [selectedCodigo, setSelectedCodigo] = useState("all");
    const [selectedResiduos, setSelectedResiduos] = useState([]);

    useEffect(() => {
        const fetchInformeCantidadesGeneradas = async () => {
            setLoading(true);
            setError(null);
            try {
                const api = getApiInstance();
                const response = await api.get(
                    `/api/Informe/GetCantidadesGeneradas`
                );
                setInformeCantidadesGeneradas(response.data);
            } catch (err) {
                setError(
                    "No se ha podido cargar el informe. Vuelve a intentarlo más tarde."
                );
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchInformeCantidadesGeneradas();
    }, []);

    const { proyectos, fechasFin, codigosObra, residuosDisponibles } =
        useMemo(() => {
            if (informeCantidadesGeneradas.length === 0) {
                return {
                    proyectos: [],
                    fechasFin: [],
                    codigosObra: [],
                    residuosDisponibles: [],
                };
            }

            const proyectosUnicos = [
                ...new Set(
                    informeCantidadesGeneradas.map((item) => item.proyecto)
                ),
            ].filter(Boolean);
            const aniosUnicos = [
                ...new Set(
                    informeCantidadesGeneradas.map((item) =>
                        new Date(item.fechaFin).getFullYear()
                    )
                ),
            ];
            const codigosUnicos = [
                ...new Set(
                    informeCantidadesGeneradas.map((item) => item.codObra)
                ),
            ];
            const residuosUnicos = [
                ...new Set(
                    informeCantidadesGeneradas.map((item) => item.residuo)
                ),
            ]
                .filter(Boolean)
                .sort();

            return {
                proyectos: [
                    { value: "all", label: "Todos los Proyectos" },
                    ...proyectosUnicos.map((p) => ({ value: p, label: p })),
                ],
                fechasFin: [
                    { value: "all", label: "Todas las Fechas fin" },
                    ...aniosUnicos.map((a) => ({
                        value: a,
                        label: a.toString(),
                    })),
                ],
                codigosObra: [
                    { value: "all", label: "Todos los Códigos de obra" },
                    ...codigosUnicos.map((c) => ({
                        value: c,
                        label: `COD-${c}`,
                    })),
                ],
                residuosDisponibles: residuosUnicos.map((r) => ({
                    value: r,
                    label: r,
                })),
            };
        }, [informeCantidadesGeneradas]);

    const datosFiltrados = useMemo(() => {
        return informeCantidadesGeneradas.filter((item) => {
            const anioFechaFin = new Date(item.fechaFin).getFullYear();
            return (
                (selectedProyecto === "all" ||
                    item.proyecto === selectedProyecto) &&
                (selectedFecha === "all" || anioFechaFin === selectedFecha) &&
                (selectedCodigo === "all" || item.codObra === selectedCodigo)
            );
        });
    }, [
        selectedProyecto,
        selectedFecha,
        selectedCodigo,
        informeCantidadesGeneradas,
    ]);

    const {
        datosTabla,
        datosBarChart,
        datosPieChart,
        totalCantidadGenerada,
        totalValorizacion,
        totalEliminacion,
        porcentajeValorizacion,
        porcentajeEliminacion,
    } = useMemo(() => {
        const datosAgrupadosTabla = Object.values(
            datosFiltrados.reduce((acc, item) => {
                if (!acc[item.residuo]) {
                    acc[item.residuo] = {
                        residuo: item.residuo,
                        pesoTotal: 0,
                        cantidadValorizada: 0,
                        cantidadEliminada: 0,
                        volumenTotal: 0,
                    };
                }
                acc[item.residuo].pesoTotal += item.pesoTotal;
                acc[item.residuo].cantidadValorizada += item.cantidadValorizada;
                acc[item.residuo].cantidadEliminada += item.cantidadEliminada;
                acc[item.residuo].volumenTotal += item.volumenTotal;
                return acc;
            }, {})
        );

        const residuosSeleccionados =
            selectedResiduos.length > 0
                ? selectedResiduos.map((r) => r.value)
                : [];

        const datosFiltradosPorResiduos =
            residuosSeleccionados.length > 0
                ? datosFiltrados.filter((item) =>
                      residuosSeleccionados.includes(item.residuo)
                  )
                : datosFiltrados;

        const agregadosBar = datosFiltradosPorResiduos.reduce((acc, item) => {
            if (!acc[item.residuo]) {
                acc[item.residuo] = {
                    cantidadEliminada: 0,
                    cantidadValorizada: 0,
                };
            }
            acc[item.residuo].cantidadEliminada += item.cantidadEliminada;
            acc[item.residuo].cantidadValorizada += item.cantidadValorizada;
            return acc;
        }, {});

        const datosProcesadosBar = Object.keys(agregadosBar).map((residuo) => ({
            name: residuo,
            Eliminación: agregadosBar[residuo].cantidadEliminada,
            Valorización: agregadosBar[residuo].cantidadValorizada,
        }));

        const totalVal = datosFiltrados.reduce(
            (sum, item) => sum + item.cantidadValorizada,
            0
        );
        const totalElim = datosFiltrados.reduce(
            (sum, item) => sum + item.cantidadEliminada,
            0
        );
        const granTotal = totalVal + totalElim;
        const porcVal = granTotal > 0 ? (totalVal / granTotal) * 100 : 0;
        const porcElim = granTotal > 0 ? (totalElim / granTotal) * 100 : 0;
        const datosProcesadosPie = [
            { name: "Valorización", value: parseFloat(porcVal.toFixed(2)) },
            { name: "Eliminación", value: parseFloat(porcElim.toFixed(2)) },
        ];

        const totalGenerado = datosFiltrados.reduce(
            (sum, item) => sum + item.pesoTotal,
            0
        );

        return {
            datosTabla: datosAgrupadosTabla,
            datosBarChart: datosProcesadosBar,
            datosPieChart: datosProcesadosPie,
            totalCantidadGenerada: totalGenerado,
            totalValorizacion: totalVal,
            totalEliminacion: totalElim,
            porcentajeValorizacion: porcVal.toFixed(1),
            porcentajeEliminacion: porcElim.toFixed(1),
        };
    }, [datosFiltrados, selectedResiduos]);

    const formatKg = (value) => new Intl.NumberFormat("es-ES").format(value);

    const tabs = [
        { id: "grafica", label: "Gráfica" },
        { id: "listado", label: "Listado" },
    ];

    const renderFilters = () => (
        <div className="filters-section-common">
            <div
                className="filter-group"
                style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "16px",
                    flexWrap: "wrap",
                    alignItems: "center",
                    padding: "16px 0",
                }}
            >
                <div style={{ flex: 1, minWidth: "200px", maxWidth: "300px" }}>
                    <InputDropdown
                        value={proyectos.find(
                            (p) => p.value === selectedProyecto
                        )}
                        options={proyectos}
                        onChange={(option) => setSelectedProyecto(option.value)}
                        placeholder="Seleccionar proyecto"
                        labelVisible={false}
                    />
                </div>
                <div style={{ flex: 1, minWidth: "200px", maxWidth: "300px" }}>
                    <InputDropdown
                        value={fechasFin.find((f) => f.value === selectedFecha)}
                        options={fechasFin}
                        onChange={(option) => setSelectedFecha(option.value)}
                        placeholder="Seleccionar fecha"
                        labelVisible={false}
                    />
                </div>
                <div style={{ flex: 1, minWidth: "200px", maxWidth: "300px" }}>
                    <InputDropdown
                        value={codigosObra.find(
                            (c) => c.value === selectedCodigo
                        )}
                        options={codigosObra}
                        onChange={(option) => setSelectedCodigo(option.value)}
                        placeholder="Seleccionar código"
                        labelVisible={false}
                    />
                </div>
            </div>
        </div>
    );

    const renderGraficaContent = () => (
        <div className="tab-content-with-filters">
            {renderFilters()}

            <div className="cantidades-generadas-content">
                <div className="graficas-card">
                    <div className="charts-section">
                        <div className="chart-container bar-chart-container">
                            <div className="chart-header">
                                <h3>Cantidad generada por Residuo</h3>
                                <div className="total-amount">
                                    {formatKg(totalCantidadGenerada)} kg
                                </div>
                            </div>

                            <div className="chart-filters">
                                <div className="residuos-filter">
                                    <InputDropdown
                                        value={selectedResiduos}
                                        options={residuosDisponibles}
                                        onChange={(selectedOptions) => {
                                            setSelectedResiduos(
                                                Array.isArray(selectedOptions)
                                                    ? selectedOptions
                                                    : []
                                            );
                                        }}
                                        placeholder="Todos los residuos"
                                        labelVisible={false}
                                        isMultiple={true}
                                        clearable={true}
                                    />
                                </div>
                            </div>

                            <div className="chart-wrapper">
                                <BarChartGraph
                                    data={{ all: datosBarChart }}
                                    activeTab="all"
                                />
                            </div>
                        </div>

                        <div className="chart-container pie-chart-container">
                            <div className="chart-header">
                                <h3>Cantidad generada</h3>
                                <div className="percentage-badge">100,00%</div>
                            </div>
                            <div className="chart-wrapper">
                                <PieChartGraph
                                    data={{ all: datosPieChart }}
                                    activeTab="all"
                                />
                            </div>
                            <div className="pie-legend">
                                <div className="pie-details">
                                    <div className="detail-line">
                                        <span>
                                            Eliminación:{" "}
                                            {formatKg(totalEliminacion)} kg. (
                                            {porcentajeEliminacion} %)
                                        </span>
                                    </div>
                                    <div className="detail-line">
                                        <span>
                                            Valorización:{" "}
                                            {formatKg(totalValorizacion)} kg. (
                                            {porcentajeValorizacion} %)
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderListadoContent = () => (
        <div className="tab-content-with-filters">
            {renderFilters()}

            <div className="listado-content">
                <Table
                    columns={columnasResiduos}
                    dataTable={datosTabla}
                    keyProp="residuo"
                    sortProp="residuo"
                    showCheckboxes={false}
                    noResultsMessage="No se han encontrado residuos con los filtros aplicados."
                    elementName="tipo de residuo"
                    loading={loading}
                    rowsPerPage={10}
                />
            </div>
        </div>
    );

    return (
        <div className="cantidades-generadas-page">
            <PageLayout
                title="Cantidades generadas por centro de producción"
                subtitle="Análisis detallado de los residuos generados y su tratamiento"
                actions={[
                    <Button
                        key="export"
                        componentType="button"
                        variant="outlined"
                        startIcon={<FiDownload />}
                    >
                        Exportar a excel
                    </Button>,
                ]}
            >
                <TabsNavigation
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                />

                {error && <div className="error-message">{error}</div>}

                {loading ? (
                    <div className="loading-message">Cargando datos...</div>
                ) : activeTab === "grafica" ? (
                    renderGraficaContent()
                ) : (
                    renderListadoContent()
                )}
            </PageLayout>
        </div>
    );
}
